// src/pages/me/negotiations/components/NegotiationInvoices.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';
import { api_url } from '../../../../constant';
import InvoiceModal from './InvoiceModal';
import { getUnits } from '../../../../api';
import { useQuery } from '@tanstack/react-query';

const NegotiationInvoices = ({ 
  negotiationId, 
  invoices, 
  isSeller, 
  negotiation,
  onInvoiceCreated,
  onInvoiceAccepted 
}) => {
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [invoiceDetails, setInvoiceDetails] = useState({
    quantity: negotiation?.quantity || '',
    amount: '',
    dueDate: '',
    notes: '',
    deliveryPlace: '',
    unitId: '',
    deliveryResponsibility: 'seller',
    paymentTerms: '',
  });

  const token = localStorage.getItem('token')?.replace(/"/g, '');
  const currentUserId = localStorage.getItem('user_id');

  const { data: units } = useQuery({
    queryKey: ['units'],
    queryFn: () => getUnits(),
    cacheTime: 99999,
    staleTime: 99999,
  });

  const hasPendingInvoice = invoices?.some(inv => inv.status === 'pending');

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">قيد الانتظار</span>;
      case 'accepted':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">مقبولة</span>;
      case 'rejected':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">مرفوضة</span>;
      default:
        return null;
    }
  };

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;
    setInvoiceDetails((prev) => ({
      ...prev,
      [name]: name === 'quantity' || name === 'amount' ? Number(value) : value,
    }));
  };

  const submitInvoice = async () => {
    try {
      const formattedNegotiationId = Number(negotiationId);
      const formattedQuantity = Number(invoiceDetails.quantity);
      const formattedAmount = Number(invoiceDetails.amount);

      if (isNaN(formattedNegotiationId) || isNaN(formattedQuantity) || isNaN(formattedAmount)) {
        toast.error('البيانات المدخلة غير صحيحة');
        return;
      }

      await axios.post(
        `${api_url}/invoices`,
        {
          negotiationId: formattedNegotiationId,
          quantity: invoiceDetails.quantity,
          amount: formattedAmount,
          dueDate: invoiceDetails.dueDate,
          notes: invoiceDetails.notes,
          deliveryPlace: invoiceDetails.deliveryPlace,
          unitId: invoiceDetails.unitId ? Number(invoiceDetails.unitId) : null,
          deliveryResponsibility: invoiceDetails.deliveryResponsibility,
          paymentTerms: invoiceDetails.paymentTerms,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('تم إصدار الفاتورة بنجاح!');
      setShowInvoiceModal(false);
      onInvoiceCreated();
      
      setInvoiceDetails({
        quantity: negotiation?.quantity || '',
        amount: '',
        dueDate: '',
        notes: '',
        deliveryPlace: '',
        unitId: '',
        deliveryResponsibility: 'seller',
        paymentTerms: '',
      });
    } catch (error) {
      console.error('Failed to submit invoice:', error);
      toast.error(error.response?.data?.message || 'فشل في إصدار الفاتورة');
    }
  };

  const handleAcceptInvoice = async (invoiceId) => {
    try {
      await axios.patch(`${api_url}/invoices/accept/${invoiceId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('تم قبول الفاتورة بنجاح!');
      onInvoiceAccepted();
    } catch (error) {
      console.error('Error accepting invoice:', error);
      toast.error('فشل في قبول الفاتورة');
    } finally {
      setShowConfirm(null);
      setSelectedInvoice(null);
    }
  };

  const handleRejectInvoice = async () => {
    if (!rejectionReason.trim()) {
      toast.error('يرجى كتابة سبب الرفض');
      return;
    }
    try {
      await axios.patch(`${api_url}/invoices/reject/${selectedInvoice.id}`, { rejectionReason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('تم رفض الفاتورة بنجاح!');
      onInvoiceAccepted();
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      toast.error('فشل في رفض الفاتورة');
    } finally {
      setShowConfirm(null);
      setSelectedInvoice(null);
      setRejectionReason('');
    }
  };

  const sortedInvoices = [...(invoices || [])].sort((a, b) => {
    const statusOrder = { accepted: 1, pending: 2, rejected: 3 };
    const statusDiff = (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
    if (statusDiff !== 0) return statusDiff;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="space-y-6">
      {isSeller && !hasPendingInvoice && negotiation?.status === 'ACTIVE' && (
        <div className="mb-6">
          <button
            onClick={() => setShowInvoiceModal(true)}
            className="w-full bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            إصدار فاتورة جديدة
          </button>
        </div>
      )}

      {isSeller && hasPendingInvoice && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 text-center">
            ⚠️ توجد فاتورة قيد الانتظار. لا يمكن إصدار فاتورة جديدة حتى يتم قبول أو رفض الفاتورة الحالية.
          </p>
        </div>
      )}

      {sortedInvoices.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-3">📄</div>
          <p>لا توجد فواتير لهذه المفاوضة</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedInvoices.map((invoice, index) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-white rounded-lg shadow-md border-r-4 ${
                invoice.status === 'accepted' ? 'border-green-500' :
                invoice.status === 'pending' ? 'border-yellow-500' :
                'border-red-500'
              }`}
            >
              <div className="p-5">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold text-gray-800">
                      فاتورة #{invoice.id}
                    </h3>
                    {getStatusBadge(invoice.status)}
                  </div>
                  <p className="text-sm text-gray-500">
                    تاريخ الإصدار: {formatDate(invoice.created_at)}
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500">الكمية</p>
                    <p className="font-semibold">{invoice.quantity} {invoice.unit?.name || 'وحدة'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">المبلغ</p>
                    <p className="font-semibold text-primary">{invoice.amount} جنيه</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">تاريخ الاستحقاق</p>
                    <p className="font-semibold">{formatDate(invoice.dueDate)}</p>
                  </div>
                </div>

                {invoice.deliveryPlace && (
                  <div className="mb-3 text-sm">
                    <span className="text-gray-500">مكان التسليم:</span> {invoice.deliveryPlace}
                  </div>
                )}

                {invoice.notes && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">ملاحظات</p>
                    <p className="text-gray-700">{invoice.notes}</p>
                  </div>
                )}

                {invoice.rejectionReason && (
                  <div className="mb-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600 mb-1">سبب الرفض</p>
                    <p className="text-red-700">{invoice.rejectionReason}</p>
                  </div>
                )}

                {invoice.status === 'pending' && !isSeller && (
                  <div className="flex gap-3 mt-4 pt-4 border-t">
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowConfirm('accept');
                      }}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition"
                    >
                      قبول الفاتورة
                    </button>
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowConfirm('reject');
                      }}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition"
                    >
                      رفض الفاتورة
                    </button>
                  </div>
                )}

                {invoice.status === 'accepted' && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-green-700">
                        ✓ تم قبول هذه الفاتورة وتم إنشاء طلب بناءً عليها
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <InvoiceModal
        showInvoiceModal={showInvoiceModal}
        setShowInvoiceModal={setShowInvoiceModal}
        invoiceDetails={invoiceDetails}
        handleInvoiceChange={handleInvoiceChange}
        submitInvoice={submitInvoice}
        units={units}
        negotiation={negotiation}
      />

      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-center">
              {showConfirm === 'accept' ? 'تأكيد قبول الفاتورة' : 'تأكيد رفض الفاتورة'}
            </h2>
            <p className="text-gray-600 mb-4 text-center">
              {showConfirm === 'accept' 
                ? 'هل أنت متأكد من قبول هذه الفاتورة؟ سيتم إنشاء طلب بعد القبول.'
                : 'هل أنت متأكد من رفض هذه الفاتورة؟'}
            </p>
            
            {showConfirm === 'reject' && (
              <textarea
                placeholder="يرجى كتابة سبب الرفض"
                className="w-full p-3 border border-gray-300 rounded-lg mb-4 text-right"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="3"
              />
            )}
            
            <div className="flex gap-3">
              <button
                onClick={showConfirm === 'accept' ? () => handleAcceptInvoice(selectedInvoice.id) : handleRejectInvoice}
                className={`flex-1 py-2 rounded-lg font-bold text-white ${
                  showConfirm === 'accept' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                تأكيد
              </button>
              <button
                onClick={() => {
                  setShowConfirm(null);
                  setSelectedInvoice(null);
                  setRejectionReason('');
                }}
                className="flex-1 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NegotiationInvoices;